import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiResponse } from 'src/app/core/models/api-response';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { getTenantId, getDepartmentId } from 'src/app/services/store-snapshot.service';
import { environment } from 'src/environments/environment';
import {
  MainTableFilterModel,
  MainTableFilterSearchByModel,
} from 'src/app/shared/models/main-table-filter.model';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { IListDocuments } from 'src/app/document-admin/models/list-documents.model';
import { TextControlService } from 'src/app/shared/text-control-module/text-control.service';
import { IdName } from 'src/app/shared/models/id-name';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { ALL_DEPS_TOKEN } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { IDocumentContent, IDocumentVersion } from '../models/document-version.model';
import {
  IApiResponseGroupDocuments,
  IDocument,
  IDocumentNavigationResponse,
  IDocumentRequest,
  IReadReceiptRequest,
} from '../models/document.model';
import { FormDataService } from '../../shared/attachments/service/form-data.service';
import { IDocumentApproval } from '../models/document-approval.model';
import { DocumentStatus } from '../enums/document-status.enum';
import { ChangeRequestStatus } from '../enums/change-request-status.enum';
import { DocumentAvailability } from '../enums/document-availability.enum';
import { IDocumentCounts } from '../models/document-counts.model';
import {
  IAdminDocumentReadReceiptRequest,
  IDocumentReadReceipt,
  IUserDocumentReadReceipt,
} from '../models/document-read-receipt.model';
import { DocumentPrintingLogoType } from '../enums/document-printing-logo-type.enum';
import { IDocumentVersionComment } from '../models/document-version-comment.model';
import {
  IDepartmentReadReceiptSetting,
  IReadReceiptSettingsResponse,
} from '../models/read-receipt-settings.model';

const COLUMN_TO_KEY_MAP: any = {
  id: 'Number',
  accessPositions: 'PositionsWithAccess.Name',
  accessPositionGroups: 'PositionGroupsWithAccess.Name',
  accessRoles: 'RolesWithAccess.Name',
  responsibleOwnerPosition: 'ResponsibleOwnerPosition.Name',
  responsibleOwnerPositionGroup: 'ResponsibleOwnerPositionGroup.Name',
  responsibleOwnerRole: 'ResponsibleOwnerRole.Name',
  status: 'Status',
  newestVersionStatus: 'NewestVersionStatus',
  readReceiptSummary: 'ReadReceiptSummarySortValue',
};

const PRIMARY_VISIBLE_STATUS_FILTER_VALUES = new Set<string>([
  `${DocumentStatus.Published}`,
  `${DocumentStatus.Expiring}`,
  `${DocumentStatus.Expired}`,
]);
const CHANGE_REQUEST_INITIATED_STATUS_FILTER_VALUE = `${DocumentStatus.ChangeRequestInitiated}`;
const OR_GROUP_MAP: Record<string, string> = {
  accessPositions: 'access',
  accessPositionGroups: 'access',
  accessRoles: 'access',
  responsibleOwnerPosition: 'responsible',
  responsibleOwnerPositionGroup: 'responsible',
  responsibleOwnerRole: 'responsible',
};

const ADMIN_READ_RECEIPT_COLUMN_TO_KEY_MAP: Record<string, string> = {
  person: 'Person',
  currentEmploymentDepartment: 'CurrentEmploymentDepartment',
  documentName: 'DocumentName',
  documentPath: 'DocumentPath',
  revisionNumber: 'RevisionNumber',
  status: 'Status',
};

const USER_READ_RECEIPT_COLUMN_TO_KEY_MAP: Record<string, string> = {
  documentName: 'DocumentName',
  path: 'Path',
  revisionNumber: 'RevisionNumber',
  created: 'Created',
  readAt: 'ReadAt',
  status: 'Status',
};

@Injectable({
  providedIn: 'root',
})
export class DocumentApiService {
  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/documents`;
  }

  private get readReceiptSettingsUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/ReadReceiptSettings`;
  }

  private get documentReadReceiptsUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/DocumentReadReceipts`;
  }

  private get printUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/documents/Print/Pdf`;
  }

  private get documentsForIOUrl(): string {
    return `${
      environment.apiUrl
    }/api/${getTenantId()}/${getDepartmentId()}/documents/Interconnected`;
  }

  constructor(private http: HttpClient) {}

  loadAllDocuments(
    currentFilter?: MainTableFilterModel,
    query?: IListDocuments
  ): Observable<ApiResponse<IDocument[]>> {
    let params = createFilterParams(currentFilter);
    if (query) {
      params = params.set('Status', query?.status);
    }

    return this.http.get<ApiResponse<IDocument[]>>(this.url, { params });
  }

  loadDocumentsByStructure(filter?: MainTableFilterModel): Observable<IApiResponseGroupDocuments> {
    if (filter?.additional?.isChangeRequest) {
      return this.getOpenChangeRequests(filter);
    }
    const url = `${
      environment.apiUrl
    }/api/${getTenantId()}/${getDepartmentId()}/documents/menuDocuments/Paged`;
    const params = this.buildMenuDocumentsListQueryParams(filter);
    return this.http.get<IApiResponseGroupDocuments>(url, { params });
  }

  getAdminReadReceiptRequests(
    filter?: MainTableFilterModel
  ): Observable<ApiResponse<IAdminDocumentReadReceiptRequest[]>> {
    let params = createFilterParams(filter, undefined, ADMIN_READ_RECEIPT_COLUMN_TO_KEY_MAP);
    params = this.appendCurrentDepartmentFilter(params);
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      filter,
      ADMIN_READ_RECEIPT_COLUMN_TO_KEY_MAP
    );

    return this.http.get<ApiResponse<IAdminDocumentReadReceiptRequest[]>>(
      `${this.documentReadReceiptsUrl}/Admin`,
      { params }
    );
  }

  getUserDocumentReadReceipts(
    userId: string,
    filter?: MainTableFilterModel
  ): Observable<ApiResponse<IUserDocumentReadReceipt[]>> {
    let params = createFilterParams(filter, undefined, USER_READ_RECEIPT_COLUMN_TO_KEY_MAP);
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      filter,
      USER_READ_RECEIPT_COLUMN_TO_KEY_MAP
    );

    return this.http.get<ApiResponse<IUserDocumentReadReceipt[]>>(
      `${this.documentReadReceiptsUrl}/Users/${userId}`,
      { params }
    );
  }

  loadDocumentById(
    documentId: string,
    includeDeleted?: boolean,
    forcedVersionId?: string
  ): Observable<IDocument> {
    const url = `${this.url}/${documentId}`;
    let params = new HttpParams();
    params = params.append('IncludeDeleted', includeDeleted ?? false);
    if (forcedVersionId) {
      params = params.append('ForcedVersionId', forcedVersionId ?? false);
    }
    return this.http.get<IDocument>(url, { params });
  }

  loadDocumentNavigation(
    documentId: string,
    filter?: MainTableFilterModel
  ): Observable<IDocumentNavigationResponse> {
    const url = `${this.url}/${documentId}/navigation`;
    let params = this.buildDocumentNavigationQueryParams(filter);
    params = params.set('DepartmentId', getDepartmentId());
    return this.http.get<IDocumentNavigationResponse>(url, { params });
  }

  loadDocumentsForIO(): Observable<IdName[]> {
    return this.http.get<IdName[]>(this.documentsForIOUrl);
  }

  createDocument(document: IDocumentRequest): Observable<IDocument> {
    const formData = FormDataService.mapToFormData(document);
    return this.http.post<IDocument>(this.url, formData);
  }

  updateDocument(document: IDocumentRequest): Observable<void> {
    const formData = FormDataService.mapToFormData(document);
    const url = `${this.url}/${document.id}`;
    return this.http.put<void>(url, formData);
  }

  deleteDocument(documentId: string): Observable<void> {
    const url = `${this.url}/${documentId}`;
    return this.http.delete<void>(url);
  }

  loadDocumentVersions(
    documentId: string,
    includeDeleted?: boolean
  ): Observable<IDocumentVersion[]> {
    const url = `${this.url}/${documentId}/versions`;
    let params = new HttpParams();
    params = params.append('IncludeDeleted', includeDeleted ?? false);
    return this.http.get<IDocumentVersion[]>(url, { params });
  }

  getDocumentVersion(
    documentId: string,
    documentVersionId: string,
    includeDeleted?: boolean
  ): Observable<IDocumentContent> {
    const url = `${this.url}/${documentId}/versions/${documentVersionId}`;
    let params = new HttpParams();
    params = params.append('IncludeDeleted', includeDeleted ?? false);
    return this.http.get<IDocumentContent>(url, { params });
  }

  getVersionComments(
    documentId: string,
    documentVersionId: string,
    onlyCurrentVersion?: boolean
  ): Observable<IDocumentVersionComment[]> {
    const url = `${this.url}/${documentId}/versions/${documentVersionId}/comments`;
    let params = new HttpParams();
    if (onlyCurrentVersion) {
      params = params.append('OnlyCurrentVersion', true);
    }
    return this.http.get<IDocumentVersionComment[]>(url, { params });
  }

  printDocumentTree(
    menuId: string,
    tableOfContents: boolean,
    printLogoType: DocumentPrintingLogoType,
    useLargeTenantLogo: boolean,
    includeDocumentId: boolean,
    isLandscape: boolean,
    useNewPdfService: boolean,
    filter: MainTableFilterModel,
    statuses?: (DocumentStatus | undefined)[],
    documentAvailability?: DocumentAvailability
  ): Observable<Blob> {
    let params = new HttpParams();
    params = params.append('MenuId', menuId);
    params = params.append('TableOfContents', tableOfContents);
    params = params.append('PrintingLogoType', printLogoType);
    params = params.append('UseLargeTenantLogo', useLargeTenantLogo);
    params = params.append('IncludeDocumentId', includeDocumentId);
    params = params.append('IsLandscape', isLandscape);
    params = params.append('UseNewPdfService', useNewPdfService);
    params = params.append('PageSize', 10000);
    if (statuses && statuses?.length > 0) {
      statuses.forEach((status: DocumentStatus | undefined) => {
        if (status === 0 || status) {
          params = params.append('Statuses', status);
        }
      });
    }
    if (documentAvailability) {
      params = params.append('DocumentAvailability', documentAvailability);
    }
    if (TextControlService.isTextControlEnabled) {
      params = params.append('IsTextControl', true);
    }
    if (filter?.orderBy && filter.ascDesc) {
      const orderByField = COLUMN_TO_KEY_MAP[filter.orderBy] || filter.orderBy;
      params = params.append(`OrderBy[${orderByField}]`, filter.ascDesc);
    }
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      this.mapDocumentStatusFilterSearch(filter),
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );

    return this.http.get(this.printUrl, { params, responseType: 'blob' });
  }

  printDocument(
    menuId: string,
    documentId: string,
    tableOfContents: boolean,
    printLogoType: DocumentPrintingLogoType,
    useLargeTenantLogo: boolean,
    includeDocumentId: boolean,
    includeHeaderInEveryPage: boolean,
    isLandscape: boolean,
    forcedVersionId?: string,
    useNewPdfService = false
  ) {
    let params = new HttpParams();
    params = params.append('MenuId', menuId);
    params = params.append('DocumentId', documentId);
    params = params.append('TableOfContents', tableOfContents);
    params = params.append('PrintingLogoType', printLogoType);
    params = params.append('UseLargeTenantLogo', useLargeTenantLogo);
    params = params.append('IncludeDocumentId', includeDocumentId);
    params = params.append('IncludeHeaderInEveryPage', includeHeaderInEveryPage);
    params = params.append('IsLandscape', isLandscape);
    params = params.append('UseNewPdfService', useNewPdfService);
    if (forcedVersionId) {
      params = params.append('ForcedVersionId', forcedVersionId ?? false);
    }
    if (TextControlService.isTextControlEnabled) {
      params = params.append('IsTextControl', true);
    }
    return this.http.get(this.printUrl, { params, responseType: 'blob' });
  }

  printTextControlOnly(documentId: string) {
    let params = new HttpParams();
    params = params.append('DocumentId', documentId);
    const textControlPDFOnlyUrl = `${this.printUrl}/Raw`;
    return this.http.get(textControlPDFOnlyUrl, { params, responseType: 'blob' });
  }

  approveRejectDocument(documentId: string, approvalRequest: IDocumentApproval) {
    const url = `${this.url}/${documentId}/Approve`;
    return this.http.put<void>(url, approvalRequest);
  }

  enableDocument(documentId: string) {
    const url = `${this.url}/${documentId}/Enable`;
    return this.http.put<void>(url, {});
  }

  disableDocument(documentId: string) {
    const url = `${this.url}/${documentId}/Disable`;
    return this.http.put<void>(url, {});
  }

  restoreDocument(documentId: string) {
    const url = `${this.url}/${documentId}/Restore`;
    return this.http.patch<void>(url, {});
  }

  getDepartmentsReadReceiptSettings(): Observable<IReadReceiptSettingsResponse[]> {
    return this.http.get<IReadReceiptSettingsResponse[]>(this.readReceiptSettingsUrl);
  }

  updateDepartmentsReadReceiptSettings(
    settings: IDepartmentReadReceiptSetting[]
  ): Observable<void> {
    return this.http.put<void>(this.readReceiptSettingsUrl, {
      departments: settings.map((setting) => ({
        departmentId: setting.departmentId,
        readReceiptsEnabled: setting.readReceiptsEnabled,
      })),
    });
  }

  confirmDocumentRead(documentId: string): Observable<void> {
    return this.http.post<void>(`${this.documentReadReceiptsUrl}/Confirm`, { documentId });
  }

  getDocumentReadReceipts(documentId: string): Observable<IDocumentReadReceipt[]> {
    const params = new HttpParams()
      .set('departmentId', getDepartmentId())
      .set('documentId', documentId);

    return this.http.get<IDocumentReadReceipt[]>(this.documentReadReceiptsUrl, { params });
  }

  updateDocumentReadReceipt(
    documentId: string,
    readReceiptId: string,
    readReceipt: IReadReceiptRequest
  ): Observable<void> {
    return this.http.put<void>(`${this.documentReadReceiptsUrl}/${documentId}/${readReceiptId}`, {
      documentId,
      readReceiptId,
      reason: readReceipt.reason,
      positionIds: readReceipt.positionIds,
      positionGroupIds: readReceipt.positionGroupIds,
      roleIds: readReceipt.roleIds,
      departmentIds: readReceipt.departmentIds,
    });
  }

  cancelDocumentReadReceiptRequest(documentId: string, requestId: string): Observable<void> {
    return this.http.post<void>(`${this.documentReadReceiptsUrl}/CancelRequest`, {
      documentId,
      requestId,
    });
  }

  deleteDocumentReadReceipt(readReceiptId: string): Observable<void> {
    return this.http.delete<void>(`${this.documentReadReceiptsUrl}/${readReceiptId}`);
  }

  createChangeRequest(documentId: string, description: string): Observable<void> {
    const url = `${this.url}/${documentId}/ChangeRequest`;
    return this.http.post<void>(url, { description });
  }

  reviewChangeRequest(
    documentId: string,
    reviewComment: string,
    requestedState: ChangeRequestStatus,
    id?: string
  ): Observable<void> {
    const url = `${this.url}/${documentId}/ChangeRequest/${id}/Review`;
    return this.http.put<void>(url, { requestedState, reviewComment });
  }

  getDocumentCounts(menuId = ''): Observable<IDocumentCounts> {
    const url = `${
      environment.apiUrl
    }/api/${getTenantId()}/${getDepartmentId()}/documents/MenuDocuments/Counts`;
    return this.http.get<IDocumentCounts>(url, { params: { menuId } });
  }

  setRevisionNumber(documentId: string, revisionNumber: number, comment: string): Observable<void> {
    const url = `${this.url}/${documentId}/SetRevisionNumber`;
    return this.http.put<void>(url, { revisionNumber, comment });
  }

  private appendCurrentDepartmentFilter(params: HttpParams): HttpParams {
    const departmentId = getDepartmentId();

    if (!departmentId || departmentId === ALL_DEPS_TOKEN) {
      return params;
    }

    return params.append('DepartmentIds', departmentId);
  }

  private mapDocumentStatusFilterSearch(
    filter?: MainTableFilterModel
  ): MainTableFilterModel | undefined {
    if (!filter?.searchBy?.length) {
      return filter;
    }
    const hasNewestVersionStatusFilter = filter.searchBy.some(
      (searchBy) => searchBy.property === 'newestVersionStatus'
    );

    return {
      ...filter,
      searchBy: filter.searchBy.flatMap((searchBy) =>
        this.mapDocumentStatusSearchBy(searchBy, hasNewestVersionStatusFilter)
      ),
    };
  }

  private mapDocumentStatusSearchBy(
    searchBy: MainTableFilterSearchByModel,
    hasNewestVersionStatusFilter: boolean
  ): MainTableFilterSearchByModel[] {
    if (searchBy.property !== 'status') {
      return [searchBy];
    }

    const values = Array.isArray(searchBy.value)
      ? searchBy.value.map(String)
      : [`${searchBy.value}`];
    const filters: MainTableFilterSearchByModel[] = [];

    if (values.length) {
      filters.push(
        new MainTableFilterSearchByModel(
          'status',
          this.getMappedStatusFilterValue(values, searchBy.value),
          searchBy.operand
        )
      );
    }

    if (values.length && !hasNewestVersionStatusFilter) {
      filters.push(
        new MainTableFilterSearchByModel(
          'newestVersionStatus',
          this.getMappedStatusFilterValue(
            this.getNewestVersionStatusValuesForPrimaryStatusFilter(values),
            searchBy.value
          ),
          searchBy.operand
        )
      );
    }

    return filters;
  }

  private getNewestVersionStatusValuesForPrimaryStatusFilter(values: string[]): string[] {
    const newestVersionStatusValues = [...values];

    if (values.some((value) => PRIMARY_VISIBLE_STATUS_FILTER_VALUES.has(value))) {
      newestVersionStatusValues.push(CHANGE_REQUEST_INITIATED_STATUS_FILTER_VALUE);
    }

    return [...new Set(newestVersionStatusValues)];
  }

  private getMappedStatusFilterValue(
    values: string[],
    originalValue: MainTableFilterSearchByModel['value']
  ): string | string[] {
    return Array.isArray(originalValue) || values.length > 1 ? values : values[0];
  }

  private getOpenChangeRequests(
    currentFilter?: MainTableFilterModel
  ): Observable<IApiResponseGroupDocuments> {
    const url = `${
      environment.apiUrl
    }/api/${getTenantId()}/${getDepartmentId()}/documents/MenuDocuments/ChangeRequests/Paged`;
    const params = this.buildMenuDocumentsListQueryParams(currentFilter);
    return this.http.get<IApiResponseGroupDocuments>(url, { params });
  }

  /** Query string shared by menu document list and change-requests list. */
  private buildMenuDocumentsListQueryParams(filter?: MainTableFilterModel): HttpParams {
    let params = createFilterParams(filter, undefined, COLUMN_TO_KEY_MAP);
    if (filter?.additional?.menuId) {
      params = params.set('MenuId', filter.additional.menuId);
    }
    if (filter?.additional?.isChangeRequest) {
      return params;
    }
    const statuses = filter?.additional?.statuses;
    if (statuses?.length) {
      statuses.forEach((status: DocumentStatus) => {
        params = params.append('Statuses', status);
      });
    }
    if (filter?.additional?.documentAvailability) {
      params = params.set('DocumentAvailability', filter.additional.documentAvailability);
    }
    return MainTableFilteringHelperService.appendFilteringParams(
      params,
      this.mapDocumentStatusFilterSearch(filter),
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );
  }

  private buildDocumentNavigationQueryParams(filter?: MainTableFilterModel): HttpParams {
    let params = createFilterParams(filter, undefined, COLUMN_TO_KEY_MAP);
    if (filter?.additional?.menuId) {
      params = params.set('MenuId', filter.additional.menuId);
    }
    if (filter?.additional?.isChangeRequest) {
      params = params.set('OnlyOpenChangeRequests', true);
    } else {
      const statuses = filter?.additional?.statuses;
      if (statuses?.length) {
        statuses.forEach((status: DocumentStatus) => {
          params = params.append('Statuses', status);
        });
      }
      if (filter?.additional?.documentAvailability) {
        params = params.set('DocumentAvailability', filter.additional.documentAvailability);
      }
    }
    return MainTableFilteringHelperService.appendFilteringParams(
      params,
      this.mapDocumentStatusFilterSearch(filter),
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );
  }
}
